"use client";

import React, { useState, useEffect, useCallback } from "react";
import {
  InstantSearch,
  Hits,
  Highlight,
  Configure,
  useSearchBox,
} from "react-instantsearch";
import { debounce } from "lodash";
import Link from "next/link";
import { useRouter } from "next/navigation";

const baseUrl = process.env.NEXT_PUBLIC_FRONTEND_URL || "https://moneyoval.com";

import type { Hit as AlgoliaHit } from "instantsearch.js";
import { Search, X } from "lucide-react";
import { Input } from "@/components/ui/input";

interface ProductHit {
  description: string;
  title: string;
  tags: string;
  slug: string;
  documentId: string;
  articlePublishedAt: string;
}

type ProductAlgoliaHit = AlgoliaHit<ProductHit>;

const Hit = ({
  hit,
  onClose,
}: {
  hit: ProductAlgoliaHit;
  onClose?: () => void;
}) => {
  const router = useRouter();
  const articleUrl = `${baseUrl}/article/${hit.slug}-${hit.documentId}`;

  const handleClick = (e: React.MouseEvent) => {
    e.preventDefault();
    router.push(articleUrl);
    if (onClose) {
      onClose(); // close modal if provided
    }
  };

  return (
    <Link href={articleUrl} onClick={handleClick}>
      <div className="p-4 mx-2 border rounded-lg mb-2 cursor-pointer hover:bg-neutral-100 dark:hover:bg-neutral-800 transition">
        <p className="text-xs text-neutral-600 dark:text-neutral-400 mb-3">
          {hit.articlePublishedAt
            ? new Date(hit.articlePublishedAt).toLocaleDateString(undefined, {
                year: "numeric",
                month: "short",
                day: "numeric",
              })
            : ""}
        </p>
        <h2 className="text-lg font-semibold line-clamp-2 mb-3 text-neutral-900 dark:text-neutral-300">
          <Highlight attribute="title" hit={hit} />
        </h2>
        <p className="text-sm text-neutral-600 dark:text-neutral-400 line-clamp-3 mb-3">
          <Highlight attribute="description" hit={hit} />
        </p>
        <p className="text-xs text-neutral-600 dark:text-neutral-400">
          <Highlight attribute="tags" hit={hit} />
        </p>
      </div>
    </Link>
  );
};

function DebouncedSearchBox(props: {
  value?: string;
  onChange?: (value: string) => void;
}) {
  const { query, clear, refine } = useSearchBox();
  const [inputValue, setInputValue] = useState(query);

  // debounce refine to delay search requests
  const debouncedRefine = useCallback(
    debounce((value: string) => {
      if (value.trim() === "") {
        clear();
        return;
      }
      refine(value);
    }, 300),
    [refine]
  );

  const onChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    setInputValue(e.currentTarget.value);
    debouncedRefine(e.currentTarget.value);
    props.onChange?.(e.currentTarget.value);
  };

  // sync local input with external query when it changes (like clearing search)
  useEffect(() => {
    setInputValue(query);
  }, [query]);

  return (
    <Input
      type="search"
      placeholder="Search articles..."
      value={inputValue}
      onChange={onChange}
      className="w-full"
      wrapperProps={{
        className: "mx-2",
      }}
      autoFocus
    />
  );
}

export default function SearchModal() {
  const [isOpen, setIsOpen] = useState(false);
  const [searchQuery, setSearchQuery] = useState("");
  const [searchClient, setSearchClient] = useState<any>(null);

  useEffect(() => {
    // body scroll lock when modal is open
    if (isOpen) {
      document.body.style.overflow = "hidden";
      // Dynamically import algoliasearch and initialize client
      import("algoliasearch").then(({ algoliasearch }) => {
        setSearchClient(
          algoliasearch(
            process.env.NEXT_PUBLIC_ALGOLIA_APP_ID || "",
            process.env.NEXT_PUBLIC_ALGOLIA_SEARCH_KEY || ""
          )
        );
      });
    }
    return () => {
      document.body.style.overflow = "unset"; // reset on unmount
    };
  }, [isOpen]);

  return (
    <>
      <button
        type="button"
        role="button"
        onClick={() => setIsOpen(true)}
        className="p-2 text-neutral-600 dark:text-neutral-300 hover:text-black cursor-pointer"
        aria-label="Open Search"
      >
        <Search className="h-5 w-5" />
      </button>

      {isOpen && (
        <div className="fixed inset-0 z-[500] bg-black/70 bg-opacity-40 flex items-center justify-center p-4 sm:p-0">
          <div className="bg-white dark:bg-neutral-900 w-full max-w-2xl max-h-[90vh] rounded-lg shadow-xl overflow-hidden flex flex-col">
            <div className="flex items-center justify-between px-4 py-3 border-b">
              <h2 className="text-lg font-medium">Search</h2>
              <button
                role="button"
                onClick={() => setIsOpen(false)}
                className="text-neutral-500 hover:text-black dark:hover:text-white transition"
                aria-label="Close Search"
              >
                <X className="h-5 w-5 text-neutral-950 dark:text-neutral-100 cursor-pointer" />
              </button>
            </div>

            <div className="p-4 overflow-y-auto flex-1">
              {searchClient && (
                <>
                  <InstantSearch
                    searchClient={searchClient}
                    indexName={process.env.NEXT_PUBLIC_ALGOLIA_INDEX_NAME || ""}
                  >
                    <Configure hitsPerPage={100} />
                    <DebouncedSearchBox
                      onChange={(e) => {
                        setSearchQuery(e);
                      }}
                    />
                    {searchQuery && searchQuery.trim() !== "" && (
                      <div className="mt-4 max-h-80 overflow-y-auto">
                        <Hits
                          hitComponent={({ hit, ...rest }) => (
                            <Hit
                              hit={hit as ProductAlgoliaHit}
                              onClose={() => {
                                setTimeout(() => {
                                  setIsOpen(false);
                                  setSearchQuery(""); // clear search query
                                  document.body.style.overflow = "unset"; // reset body overflow
                                }, 1000); // slight delay to allow click event to propagate
                              }}
                              {...rest}
                            />
                          )}
                        />
                      </div>
                    )}
                  </InstantSearch>
                </>
              )}
            </div>
          </div>
        </div>
      )}
    </>
  );
}
